import React, { useRef, useEffect, useMemo, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import styled from "@emotion/styled";
import { MDSSession } from "@/mds/components/sessions/session";
import {
  IMDSSessionProps,
  IMDSSessionRef,
  SessionEvent,
  SessionFeature,
  SessionStatus,
  FactSourceKind,
  ResolvedFactSource,
} from "@/mds/components/sessions/session.types";
import { useAppStore } from "@/store";
import { MemCommonMentionKind, MemCommonEditorAssetProxyConfig } from "@mem-labs/common-editor";
import { getBackendAssetProxyUrl } from "@/manager/backend-api-client/initializeBackendApiManager";
import { SessionObservable } from "@/store/sessions/SessionObservable";
import {
  MDSButtonGroup,
  MDSComboboxVariant,
  MDSIconButton,
  MDSTooltip,
  MDSTooltipPlacement,
  MDSSidePanelHeader,
  MDSSidePanelHeaderSlotName,
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  useTheme,
  MDSTextColor,
  MDSIconKind,
  MDSMenuDropdownSlotName,
  MDSActionMenuDropdown,
} from "@/mds";
import { FloatingNotePreview, ItemPreviewHoveringState } from "@/components/item-preview";
import { Maybe } from "@/domains/common/types";
import { INoteObservable } from "@/store/note";
import { logger } from "@/modules/logger";
import { objectModule } from "@/modules/object";
import { SessionEventModelData } from "@/store/sessions/types";
import { MemItFeedbackModal } from "./feedback/mem-it-feedback-modal";
import EmptyOverlay from "./empty-overlay";
import { SpaceAccountPostHogFeatureFlagChatZeroStateVariant } from "@/store/feature-flags/types";
import { checkIfIsOnboarding } from "./empty-overlay.utils";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import {
  MDSComboboxMention,
  MDSComboboxDropdownConfig,
} from "@/mds/components/forms/combobox/combobox.types";
import { AppStore } from "@/store/AppStore";
import { MemItPageStore } from "@/store/pages/MemItPageStore";
import { ChatModel, MODEL_OPTIONS, CHAT_MODEL_LABELS } from "@/domains/chat-model";
import { isEditableElement } from "@/modules/html";
import { PdfLightbox, ImageLightbox } from "@/components/note/media-viewer";
import { clientEnvIsMobileBrowser } from "@/modules/client-env/isMobileBrowser";
import { windowModule } from "@/modules/window";
import { requestStartHuddleMode } from "@/domains/huddle/huddle-mode-events";
import { HuddleModeLauncher } from "@/domains/huddle/huddle-mode-launcher";

const MEM_IT_INPUT_FOCUS_RETRY_INTERVAL_MS = 100;
const MEM_IT_INPUT_FOCUS_RETRY_TIMEOUT_MS = 2500;
const MODEL_DROPDOWN_DEFAULT_WIDTH_PX = 176;
const MODEL_DROPDOWN_FREE_USER_WIDTH_PX = 220;

const isMemItEditableElementFocused = (container: HTMLElement | null) => {
  // ignore missing containers
  if (!container) {
    return false;
  }

  const activeElement = document.activeElement;
  // confirm the focused editor belongs to Mem It
  return isEditableElement(activeElement) && container.contains(activeElement);
};

export const getModelDropdownConfig = (
  _store: AppStore,
  memItStore: MemItPageStore
): MDSComboboxDropdownConfig | undefined => {
  // When session has messages AND user is paid, show the new "Start a new chat" layout:
  // - Selected model at top with checkmark
  // - "Start a new chat" section header
  // - Remaining models below (no badges, no divider)
  const showNewChatLayout = !memItStore.canChangeModel && memItStore.isPaidUser;

  const isLegacyModel = !MODEL_OPTIONS.some(opt => opt.id === memItStore.selectedModel);

  const buildOptions = () => {
    if (showNewChatLayout) {
      const selected = MODEL_OPTIONS.find(opt => opt.id === memItStore.selectedModel) ?? {
        id: memItStore.selectedModel,
        label: CHAT_MODEL_LABELS[memItStore.selectedModel] ?? "Unknown",
        icon: MDSIconKind.Anthropic,
      };
      const rest = MODEL_OPTIONS.filter(opt => opt.id !== memItStore.selectedModel);
      return [selected!, ...rest].map(opt => ({
        ...opt,
        showBadge: false,
        badgeLabel: undefined,
      }));
    }

    const options = MODEL_OPTIONS.map((opt, index) => {
      const isPremiumModel = index > 0;
      const isSelected = opt.id === memItStore.selectedModel;

      let showBadge = false;
      let badgeLabel: string | undefined;

      if (!isSelected) {
        if (isPremiumModel && !memItStore.isPaidUser) {
          showBadge = true;
          badgeLabel = "Pro";
        }
      }

      return {
        ...opt,
        showBadge,
        badgeLabel,
      };
    });

    if (isLegacyModel) {
      options.unshift({
        id: memItStore.selectedModel,
        label: CHAT_MODEL_LABELS[memItStore.selectedModel] ?? "Unknown",
        icon: MDSIconKind.Anthropic,
        showBadge: false,
        badgeLabel: undefined,
      });
    }

    return options;
  };

  return {
    options: buildOptions(),
    selectedId: memItStore.selectedModel,
    defaultOptionId: ChatModel.Auto,
    dropdownWidthPx: memItStore.isPaidUser
      ? MODEL_DROPDOWN_DEFAULT_WIDTH_PX
      : MODEL_DROPDOWN_FREE_USER_WIDTH_PX,
    sectionHeader: showNewChatLayout ? "Start a new chat" : undefined,
    dividerAfterIndex: showNewChatLayout ? undefined : isLegacyModel ? 1 : 0,
    onOpenChange: (isOpen: boolean) => {
      if (!isOpen) return;

      trackEvent(TrackedEvent.ChatModelDropdownOpen, {
        currentModel: memItStore.selectedModel,
      });
    },
    onSelect: (optionId: string) => {
      const selectedOption = MODEL_OPTIONS.find(opt => opt.id === optionId);
      const isPremiumModel = MODEL_OPTIONS.indexOf(selectedOption!) > 0;

      // If it's a premium model and user is not paid, trigger upsell
      const choseOutOfTierModel = isPremiumModel && !memItStore.isPaidUser;

      const previousModel = memItStore.selectedModel;
      const startsNewChat = !memItStore.canChangeModel;
      trackEvent(TrackedEvent.ChatModelSelect, {
        selectedModel: optionId,
        previousModel,
        startsNewChat,
        choseOutOfTierModel,
      });

      if (choseOutOfTierModel) {
        memItStore.handlePremiumModelClick();
        return;
      }

      // If we can change the model in the current session, just update it
      if (memItStore.canChangeModel) {
        memItStore.handleModelSelect(optionId as ChatModel);
      } else {
        // Otherwise, start a new chat with the selected model
        memItStore.handleNewChatWithModel(optionId as ChatModel);
      }
    },
  };
};

interface MemItSidePanelViewProps {
  sessionId?: string;
  inSidePanel?: boolean;
  isClosed?: boolean;
  onClose?: () => void;
}

export const MemItActions = observer(() => {
  const { pageStore } = useAppStore();
  const memItStore = pageStore.memItPage;

  const hasPreviousSessions = useMemo(() => {
    return memItStore.previousChatSessions.length > 0;
  }, [memItStore.previousChatSessions]);

  const contentList = useMemo(() => {
    return memItStore.previousSessionsContentList;
  }, [memItStore.previousSessionsContentList]);

  const newSessionLabel = "New chat";
  const previousSessionsLabel = "Past chats";

  return (
    <>
      <MDSTooltip
        config={{
          label: newSessionLabel,
          placement: MDSTooltipPlacement.Top,
        }}
      >
        <MDSIconButton iconKind={MDSIconKind.Plus} onClick={memItStore.handleNewSession} />
      </MDSTooltip>
      {hasPreviousSessions && (
        <MDSTooltip config={{ label: previousSessionsLabel, placement: MDSTooltipPlacement.Top }}>
          <MDSActionMenuDropdown contentList={contentList}>
            <MDSIconButton slot={MDSMenuDropdownSlotName.Trigger} iconKind={MDSIconKind.History} />
          </MDSActionMenuDropdown>
        </MDSTooltip>
      )}
    </>
  );
});

export const MemItSidePanelView = observer<MemItSidePanelViewProps>(
  ({ sessionId, isClosed = false, onClose }) => {
    const { theme } = useTheme();
    const { store, pageStore } = useAppStore();
    const memItPageStore = pageStore.memItPage;
    // track the Mem It container for focus checks
    const containerRef = useRef<HTMLDivElement>(null);
    const handleHuddleStartRequest = useCallback(() => {
      if (isClosed) {
        store.sidePanel.goToMemIt(memItPageStore.session?.id ?? sessionId);
      }
    }, [isClosed, memItPageStore.session?.id, sessionId, store.sidePanel]);

    useEffect(() => {
      // Explicitly initialize route mentions on mount (MobX reaction handles subsequent changes)
      memItPageStore.initializeCurrentObservableFromRoute();
    }, [memItPageStore]);

    useEffect(() => {
      // Sync the store's active session after render commit to avoid setState during render
      if (sessionId) {
        memItPageStore.setSessionId(sessionId);
      }
    }, [memItPageStore, sessionId]);

    useChatPanelViewTracking({ isClosed });

    return (
      <Container ref={containerRef} data-component="MemItSidePanelView" $isInteractive={isClosed}>
        <PanelHeaderContainer $isClosed={isClosed} $isInteractive={isClosed}>
          <MDSSidePanelHeader
            backgroundColor={theme.colors.surface.chat}
            bottomBorderColor={theme.colors.border.onAccent}
          >
            <MDSText
              slot={MDSSidePanelHeaderSlotName.Heading}
              variant={MDSTextVariant.TextS}
              weight={MDSTextWeight.Smedium}
              color={MDSTextColor.Contrast}
            >
              Chat
            </MDSText>
            {!isClosed && (
              <MDSButtonGroup
                slot={MDSSidePanelHeaderSlotName.Actions}
                data-test-id="mem-it-actions-group"
              >
                <MemItActions />
              </MDSButtonGroup>
            )}
          </MDSSidePanelHeader>
        </PanelHeaderContainer>
        {memItPageStore.session && (
          <Inner
            containerRef={containerRef}
            sessionObservable={memItPageStore.session}
            isClosed={isClosed}
            onClose={onClose}
            mentions={memItPageStore.currentMentions}
          />
        )}
        <HuddleModeLauncher onStartRequest={handleHuddleStartRequest} />
      </Container>
    );
  }
);

const Inner = observer(
  ({
    containerRef,
    sessionObservable,
    isClosed = false,
    mentions = [],
  }: {
    containerRef: React.RefObject<HTMLElement | null>;
    sessionObservable: SessionObservable;
    isClosed?: boolean;
    onClose?: () => void;
    mentions?: MDSComboboxMention[];
  }) => {
    const { store, pageStore } = useAppStore();
    const [hoverState] = useState(new ItemPreviewHoveringState());
    const [hoveredNote, setHoveredNote] = useState<Maybe<INoteObservable>>();
    const [hoveredText, setHoveredText] = useState<string | undefined>(undefined);
    const [isFeedbackModalOpen, setIsFeedbackModalOpen] = useState(false);
    const [isFeedbackPositive, setIsFeedbackPositive] = useState(false);
    const [currentFeedbackEventId, setCurrentFeedbackEventId] = useState<string | undefined>();
    const [pdfLightboxState, setPdfLightboxState] = useState<{
      src: string;
      fileName?: string;
      initialPage?: number;
    } | null>(null);
    const [imageLightboxState, setImageLightboxState] = useState<{
      src: string;
    } | null>(null);

    const memItInputFocusRequestKey = store.sidePanel.memItInputFocusRequestKey;
    const onLeftAddonButtonClick = useCallback(() => {
      if (!isClosed) return;
      store.sidePanel.goToMemIt(sessionObservable.id);
    }, [isClosed, store, sessionObservable.id]);

    const memItStore = pageStore.memItPage;
    const shouldShowHuddleEntry =
      store.spaceAccountFeatureFlags.getFlagStatus("huddle-mode") ?? false;
    const chatInputPlaceholder = shouldShowHuddleEntry
      ? "Ask or tell Mem something"
      : "Tell or ask Mem something";

    const handleFeedback = useCallback(
      async (eventId: string, responseKind: "UPVOTE" | "DOWNVOTE") => {
        try {
          // Submit feedback immediately when user clicks
          memItStore.provideFeedback(eventId, responseKind);

          // Then show modal to collect additional details
          setCurrentFeedbackEventId(eventId);
          setIsFeedbackPositive(responseKind === "UPVOTE");
          setIsFeedbackModalOpen(true);
        } catch (error) {
          logger.error({
            message: "Failed to provide initial MemIt feedback",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        }
      },
      [memItStore]
    );

    const handleCloseFeedbackModal = useCallback(() => {
      setIsFeedbackModalOpen(false);
      setCurrentFeedbackEventId(undefined);
    }, []);

    const handleSubmitFeedback = useCallback(
      async (suggestedFeedback: string | undefined, userText: string) => {
        if (!currentFeedbackEventId) return;

        try {
          const responseKind = isFeedbackPositive ? "UPVOTE" : "DOWNVOTE";
          const selectedSuggestions = suggestedFeedback ? [suggestedFeedback] : undefined;

          // Submit additional feedback details (this updates the existing feedback)
          await memItStore.provideFeedback(
            currentFeedbackEventId,
            responseKind,
            userText || undefined,
            selectedSuggestions
          );

          setIsFeedbackModalOpen(false);
          setCurrentFeedbackEventId(undefined);
        } catch (error) {
          logger.error({
            message: "Failed to provide additional MemIt feedback details",
            info: { error: objectModule.safeErrorAsJson(error as Error) },
          });
        }
      },
      [currentFeedbackEventId, isFeedbackPositive, memItStore]
    );

    const sessionRef = useRef<IMDSSessionRef>(null);
    const previousSessionIdRef = useRef<string | undefined>(undefined);
    const lastHandledMemItInputFocusRequestKeyRef = useRef(0);
    const sessionData = sessionObservable.modelData;

    // If the current session is empty, and our panel is open, show the empty state.
    // If the pane is closed, we show the Session because it shows the chat message ui
    // at the bottom of the panel when another panel is open.
    // Don't show empty state if there's a pending new chat message about to be sent
    const showEmptyState =
      memItStore.sessionEvents.length === 0 && !isClosed && !memItStore.isPendingNewChat;

    // clear the pending-new-chat state since we're ready to show it
    useEffect(() => {
      if (memItStore.isPendingNewChat && memItStore.sessionEvents.length > 0) {
        setTimeout(() => {
          memItStore.clearIsPendingNewChat();
        }, 0);
      }
    }, [memItStore, showEmptyState]);

    // Swap the session when it changes
    useEffect(() => {
      const currentSessionId = sessionObservable.id;
      const previousSessionId = previousSessionIdRef.current;

      // Set the current session on the session context when it changes in
      // the observable which is held in the page store.
      if (previousSessionId !== currentSessionId) {
        sessionRef.current?.setCurrentSessionId(currentSessionId);

        // Update the ref
        previousSessionIdRef.current = currentSessionId;
      }
    }, [sessionObservable.id]);

    // Upsert the session events into the session context when they change on the observable.
    useEffect(() => {
      const sessionEvents = exportSessionEvents(memItStore.sessionEvents);
      sessionRef.current?.upsertEvents(sessionEvents);
    }, [memItStore.sessionEvents]);

    // Update the session status in the session context when it changes on the observable.
    useEffect(() => {
      if (sessionData?.status) {
        sessionRef.current?.updateStatus(sessionData.status as SessionStatus);
      }
    }, [sessionData?.status]);

    useEffect(() => {
      if (isClosed) {
        return;
      }

      // skip handled requests
      if (memItInputFocusRequestKey <= lastHandledMemItInputFocusRequestKeyRef.current) {
        return;
      }

      const attemptFocus = () => {
        // ask the session input to focus
        sessionRef.current?.focusComboboxInput();
        if (isMemItEditableElementFocused(containerRef.current)) {
          // mark the request as handled once focus sticks
          lastHandledMemItInputFocusRequestKeyRef.current = memItInputFocusRequestKey;
          return true;
        }

        return false;
      };

      // stop once the first attempt works
      if (attemptFocus()) {
        return;
      }

      // keep retrying while the panel finishes opening
      const intervalId = window.setInterval(() => {
        if (attemptFocus()) {
          window.clearInterval(intervalId);
        }
      }, MEM_IT_INPUT_FOCUS_RETRY_INTERVAL_MS);

      // give up after a short window
      const timeoutId = window.setTimeout(() => {
        lastHandledMemItInputFocusRequestKeyRef.current = memItInputFocusRequestKey;
        window.clearInterval(intervalId);
      }, MEM_IT_INPUT_FOCUS_RETRY_TIMEOUT_MS);

      return () => {
        window.clearInterval(intervalId);
        window.clearTimeout(timeoutId);
      };
    }, [containerRef, isClosed, memItInputFocusRequestKey]);

    const linkHandler = (
      kind: MemCommonMentionKind,
      id: string,
      target: HTMLElement | null,
      event: React.MouseEvent<Element>,
      meta?: { noteVersion?: number | null }
    ) => {
      event.preventDefault();
      store.navigation.goToMention({
        id: id,
        mentionKind: kind,
        url: `${kind}/${id}`,
        highlightText: target?.textContent ?? undefined,
        keys: event,
        historyVersion: meta?.noteVersion ? meta.noteVersion : undefined,
        suppressSidePanelRouteHandler: true,
      });
    };

    const handleMemItemOver = useCallback(
      (itemKind: MemCommonMentionKind, itemId: string, target: HTMLElement | null) => {
        if (itemKind !== MemCommonMentionKind.Note) {
          return;
        }

        if (!target) {
          return;
        }

        hoverState.setHoveredRect(target?.getBoundingClientRect());
        setHoveredText(target?.innerText);
        setHoveredNote(store.notes.getNoteObservableById({ noteId: itemId }));
        hoverState.handleMouseEnter();
      },
      [store.notes, hoverState]
    );

    const handleMemItemOut = useCallback(() => {
      hoverState.setHoveredRect(null);
      setHoveredNote(undefined);
      hoverState.handleMouseLeave();
    }, [hoverState]);

    const resolveFactSource = useCallback(
      async (kind: FactSourceKind, id: string): Promise<ResolvedFactSource | null> => {
        if (kind === "NOTE") {
          const noteObservable = await store.notes.getAsync(id);
          if (!noteObservable) return null;
          return {
            id,
            label: noteObservable.title,
            kind: "NOTE",
          };
        }

        if (kind === "COLLECTION") {
          const collectionObservable = await store.collections.getAsync(id);
          if (!collectionObservable) return null;
          return {
            id,
            label: collectionObservable.label,
            kind: "COLLECTION",
          };
        }

        return null;
      },
      [store.notes, store.collections]
    );

    const resolveAssetProxy = useCallback(async (): Promise<
      MemCommonEditorAssetProxyConfig | undefined
    > => {
      const authStore = store.publicAppStore.auth;
      if (!authStore.isStandardMode) return undefined;

      const token = await authStore.assetAccessTokenState.ensureToken();
      if (!token) return undefined;

      const expiresAtIso = authStore.assetAccessTokenState.expiresAt
        ? authStore.assetAccessTokenState.expiresAt.toUTC().toISO()
        : null;

      return {
        baseUrl: getBackendAssetProxyUrl(),
        auth: { assetAccessToken: token, expiresAt: expiresAtIso },
      };
    }, [store.publicAppStore.auth]);

    // Intercept attachment link clicks on desktop to open in lightbox viewers.
    // After render-time URL resolution, fragment markers identify the type:
    //   #mem-pdf-page=N  → PDF at specific page
    //   #mem-pdf         → PDF without page
    //   #mem-img         → Image
    const handleClickLink = useCallback(
      (href: string, _target: HTMLElement, _event: React.MouseEvent<Element>): boolean => {
        const hashIndex = href.lastIndexOf("#");
        if (hashIndex === -1) return false;

        const hash = href.slice(hashIndex + 1);
        const srcWithoutHash = href.slice(0, hashIndex);

        const isAttachment = hash.startsWith("mem-pdf") || hash === "mem-img";
        if (!isAttachment) return false;

        // Mobile web: open native viewer in new tab
        if (clientEnvIsMobileBrowser()) {
          const pdfPage = hash.match(/^mem-pdf-page=(\d+)$/);
          const nativeUrl = pdfPage ? `${srcWithoutHash}#page=${pdfPage[1]}` : srcWithoutHash;
          windowModule.openInNewTab({ url: new URL(nativeUrl) });
          return true;
        }

        // Desktop: open in lightbox
        const pdfPageMatch = hash.match(/^mem-pdf-page=(\d+)$/);
        if (pdfPageMatch) {
          setPdfLightboxState({ src: srcWithoutHash, initialPage: parseInt(pdfPageMatch[1], 10) });
          return true;
        }

        if (hash === "mem-pdf") {
          setPdfLightboxState({ src: srcWithoutHash });
          return true;
        }

        if (hash === "mem-img") {
          setImageLightboxState({ src: srcWithoutHash });
          return true;
        }

        return false;
      },
      []
    );

    const handleClosePdfLightbox = useCallback(() => {
      setPdfLightboxState(null);
    }, []);

    const handleCloseImageLightbox = useCallback(() => {
      setImageLightboxState(null);
    }, []);

    const sessionProps: IMDSSessionProps = {
      sessionTitle: "Mem It",
      sessionSubtitle: `Session: ${sessionObservable.id}`,
      initialSessionEvents: [],
      initialSessionStatus: (sessionData?.status as SessionStatus) || SessionStatus.Initial,
      containerHeight: 400, // Smaller height for sidebar
      isOpen: !isClosed,
      config: {
        features: [SessionFeature.ContextLimitExceededNewChatButton, SessionFeature.AllowCopy],
        openLinksInNewTab: false,
        linkHandler: linkHandler,
        onMemItemOver: handleMemItemOver,
        onMemItemOut: handleMemItemOut,
        feedbackHandler: handleFeedback,
        resolveFactSource: resolveFactSource,
        resolveAssetProxy,
        onClickLink: handleClickLink,
      },
      comboboxProps: {
        placeholder: chatInputPlaceholder,
        variant: MDSComboboxVariant.Brand,
        chatIsClosed: isClosed,
        forceCollapse: isClosed,
        fetchIsInstant: true,
        mentions: mentions,
        implicitMentions: memItStore.currentImplicitMentions,
        onMentionAdd: memItStore.handleMentionAdd,
        onMentionRemove: memItStore.handleMentionRemove,
        onMentionsClear: memItStore.handleMentionsClear,
        onLeftAddonButtonClick: onLeftAddonButtonClick,
        emptyActionButton: shouldShowHuddleEntry
          ? {
              ariaLabel: "Start Huddle",
              dataTestId: "chat-huddle-mic-button",
              iconKind: MDSIconKind.Microphone,
              onPress: requestStartHuddleMode,
            }
          : undefined,
        dropdownConfig: getModelDropdownConfig(store, memItStore),
      },
      handlers: {
        handleFetchComboboxData: memItStore.handleComboboxFetch,
        handleInputFormSubmit: memItStore.handleCreateUserMessage,
        handleNewSession: memItStore.handleNewSession,
        handleViewModeChanged: () => {},
        handleCountdownCompleted: () => {},
        handleRetry: async () => {
          // Clear existing session error before retry attempt
          sessionRef.current?.clearSessionError();
          await store.sessions.processSession(sessionObservable.id);
        },
      },
    };

    return (
      <>
        <SessionContainer data-component="SessionContainer">
          {showEmptyState && (
            <EmptyOverlay currentSessionId={sessionObservable.id} isClosed={isClosed} />
          )}
          <MDSSession ref={sessionRef} key={sessionObservable.id} {...sessionProps} />
        </SessionContainer>
        <MemItFeedbackModal
          isModalOpen={isFeedbackModalOpen}
          handleCloseModal={handleCloseFeedbackModal}
          isPositive={isFeedbackPositive}
          onSubmit={handleSubmitFeedback}
        />
        <FloatingNotePreview
          state={hoverState}
          noteObservable={hoveredNote}
          placement="right"
          highlightText={hoveredText}
        />
        <PdfLightbox
          isOpen={!!pdfLightboxState}
          src={pdfLightboxState?.src ?? ""}
          fileName={pdfLightboxState?.fileName}
          initialPage={pdfLightboxState?.initialPage}
          context="chat"
          handleClose={handleClosePdfLightbox}
          onOpenChange={open => {
            if (!open) handleClosePdfLightbox();
          }}
        />
        <ImageLightbox
          isOpen={!!imageLightboxState}
          src={imageLightboxState?.src}
          context="chat"
          handleClose={handleCloseImageLightbox}
          onOpenChange={open => {
            if (!open) handleCloseImageLightbox();
          }}
        />
      </>
    );
  }
);

const Container = styled.div<{ $isInteractive: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: ${({ $isInteractive }) => ($isInteractive ? "pointer" : "default")};
  border-top-right-radius: ${({ theme }) => theme.borderRadius.xlarge};
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const SessionContainer = styled.div`
  flex: 1;
  overflow: auto;
  background-color: ${({ theme }) => theme.colors.surface.chat};
`;

const exportSessionEvents = (sessionEvents: SessionEventModelData[]): SessionEvent[] => {
  return sessionEvents.map(event => ({
    id: event.id,
    session_id: event.session_id,
    locally_created_at: event.locally_created_at,
    internal_created_at: event.locally_created_at,
    internal_updated_at: event.locally_created_at,
    feedbackGiven: event.feedback_given,
    data: {
      kind: event.kind,
      value: event.value_json,
    } as SessionEvent["data"],
  }));
};

const useChatPanelViewTracking = ({ isClosed }: { isClosed: boolean }) => {
  const { store } = useAppStore();
  const isOnboarding = checkIfIsOnboarding({ store });
  const isOnboardingFlowActive = store.onboarding.isActive;

  useEffect(() => {
    // Track ChatPanelView only once per browser session when we open the panel
    // and no onboarding flow is currently active
    if (isClosed) return;
    if (isOnboardingFlowActive) return;

    let variant = SpaceAccountPostHogFeatureFlagChatZeroStateVariant.AskAboutYourNotes;
    if (isOnboarding) {
      variant = SpaceAccountPostHogFeatureFlagChatZeroStateVariant.Onboarding;
    }

    logger.info({
      message: "[AppStoreTrackingStore]: Tracking chat_panel_view event",
      info: {
        variant,
      },
    });

    trackEvent(TrackedEvent.ChatPanelView, { variant });
  }, [isClosed, isOnboarding, isOnboardingFlowActive]);
};

const PanelHeaderContainer = styled.div<{ $isClosed: boolean; $isInteractive: boolean }>`
  position: relative;
  overflow: hidden;
  max-height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  min-height: ${({ $isClosed }) => ($isClosed ? "0px" : "auto")};
  transition: height 0.16s ease;
`;
